import React from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
// Importing ./i18n initializes the shared i18next instance synchronously
// (bundled catalogs), so the language is resolved before the first render.
import { getCurrentLocale, i18n } from './i18n';
import { applyDocumentLang } from './i18n/locale';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { ActiveGameProvider } from './context/ActiveGameContext';
import { initNativeChrome } from './utils/nativeChrome';
import { initNativePushDeepLinking } from './utils/nativePushDeepLink';
import { initNativeBackButton } from './utils/nativeBackButton';
import './index.css';

export const APP_BUILD_MARKER = 'tiles-redeploy-check-2026-06-29';

// index.html ships a static lang="en"; align it with the detected locale
// before paint (runtime changes stay in sync via the i18n module's listener).
applyDocumentLang(getCurrentLocale());

// Native only (no-op on web): edge-to-edge status bar + icon style, see
// utils/nativeChrome.ts for why this must be explicit rather than left to
// each Android version's own default.
initNativeChrome();

// Native only (no-op on web): tapping a push notification opens the right
// screen (see utils/nativePushDeepLink.ts) — registered before the first
// render so a cold-start launch-by-notification tap is still caught.
initNativePushDeepLinking();

// Native only (no-op on web): the Android hardware back button/gesture goes
// through the SAME navigation path as browser back (see
// utils/nativeBackButton.ts), including the existing leave-game guard.
initNativeBackButton();

const container = document.getElementById('root')!;
// Providers wrap the router. They use no router hooks (verified), so they sit
// ABOVE RouterProvider (inside <App/>) yet still provide context to every route.
createRoot(container).render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <AuthProvider>
        <ConfirmProvider>
          <ActiveGameProvider>
            <App />
          </ActiveGameProvider>
        </ConfirmProvider>
      </AuthProvider>
    </I18nextProvider>
  </React.StrictMode>
);
